import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DatePicker,
  Stack,
} from '@razz-team/ui-web';
import { addDays, differenceInCalendarDays, subDays } from 'date-fns';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

const fromDateDefault = subDays(new Date(), 1);
const toDateDefault = new Date();

export function StatementsDateRangeDialog({
  onClose,
  open,
  onSubmit,
}: {
  onClose: () => void;
  open: boolean;
  onSubmit: (value: { from: Date; to: Date }) => void;
}) {
  const { t } = useTranslation();
  const [fromDate, setFromDate] = useState<Date | null>(fromDateDefault);
  const [toDate, setToDate] = useState<Date | null>(toDateDefault);

  const handleClose = useCallback(() => {
    setFromDate(fromDateDefault);
    setToDate(toDateDefault);
    onClose();
  }, [onClose]);

  const handleChangeFromDate = useCallback(
    (value: Date | null) => {
      if (toDate && value && differenceInCalendarDays(toDate, value) <= 0) {
        setFromDate(value);
        setToDate(addDays(value, 1));
      } else {
        setFromDate(value);
      }
    },
    [toDate],
  );

  const handleChangeToDate = useCallback(
    (value: Date | null) => {
      if (fromDate && value && differenceInCalendarDays(value, fromDate) <= 0) {
        setToDate(value);
        setFromDate(subDays(value, 1));
      } else {
        setToDate(value);
      }
    },
    [fromDate],
  );

  const handleSubmit = useCallback(() => {
    if (toDate && fromDate) {
      onSubmit({ from: fromDate, to: toDate });
      onClose();
    }
  }, [fromDate, onClose, onSubmit, toDate]);

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>{t('Select date range')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} paddingTop={2} direction="row">
          <DatePicker
            value={fromDate}
            label="From"
            format="dd/MM/yyyy"
            onChange={handleChangeFromDate}
            maxDate={subDays(new Date(), 1)}
          />
          <DatePicker
            value={toDate}
            label="To"
            format="dd/MM/yyyy"
            onChange={handleChangeToDate}
            maxDate={new Date()}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          {t('Cancel')}
        </Button>
        <Button onClick={handleSubmit} disabled={!fromDate || !toDate}>
          {t('Select')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
